import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

/**
 * Admin access check hook
 * 
 * ⚠️ SECURITY NOTE ⚠️
 * Development bypass is ONLY available on localhost with DEV mode enabled.
 * All preview URLs and production URLs require full authentication + 2FA.
 */
export const useAdminCheck = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const checkAdminRole = async () => {
      const hostname = window.location.hostname;
      
      // SECURITY: Only allow dev bypass on localhost AND when in development mode
      // Preview URLs (.lovable.app, .lovable.dev) are publicly accessible and must NOT bypass auth
      const isDevelopment = hostname === 'localhost' && import.meta.env.DEV;

      console.log('🔍 Admin Check Debug:', {
        hostname,
        isDevelopment,
        user: user ? 'present' : 'null',
        path: window.location.pathname
      });

      // DEVELOPMENT ONLY: Grant access on localhost in dev mode
      if (isDevelopment) {
        console.warn(
          '🔓 DEVELOPMENT MODE ACTIVE (localhost only)\n' +
          '→ Admin access granted for development\n' +
          '→ This bypass is DISABLED on all deployed URLs'
        );
        setIsAdmin(true);
        setIsLoading(false);
        return;
      }

      // PRODUCTION & PREVIEW: Normal authentication flow with 2FA
      console.log('🔒 Production/Preview mode - checking authentication');
      
      if (!user) {
        console.log('❌ No user found, redirecting to /auth');
        navigate("/auth");
        setIsLoading(false);
        return;
      }

      try {
        // Check if user has admin role
        const { data: roleData, error: roleError } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id)
          .eq("role", "admin")
          .maybeSingle();

        if (roleError || !roleData) {
          console.log('❌ User is not admin, redirecting to /');
          navigate("/");
          setIsLoading(false);
          return;
        }

        // Check for valid 2FA login session
        const sessionToken = localStorage.getItem("admin_session_token");
        const sessionExpires = localStorage.getItem("admin_session_expires");

        if (!sessionToken || !sessionExpires) {
          console.log('❌ No 2FA session found, redirecting to verification');
          sessionStorage.setItem('pending_admin_verification', 'true');
          sessionStorage.setItem('admin_email', user.email || '');
          navigate("/admin/verify-login");
          setIsLoading(false);
          return;
        }

        // Check if session is expired
        if (new Date(sessionExpires) < new Date()) {
          console.log('❌ 2FA session expired, redirecting to verification');
          localStorage.removeItem("admin_session_token");
          localStorage.removeItem("admin_session_expires");
          sessionStorage.setItem('pending_admin_verification', 'true');
          sessionStorage.setItem('admin_email', user.email || '');
          navigate("/admin/verify-login");
          setIsLoading(false);
          return;
        }

        // Validate session token server-side using secure function
        // This prevents session tokens from being exposed to the client
        const { data: isValid, error: sessionError } = await supabase
          .rpc("validate_admin_session", {
            p_session_token: sessionToken,
            p_user_id: user.id
          });

        if (sessionError || !isValid) {
          console.log('❌ Invalid 2FA session, redirecting to verification');
          localStorage.removeItem("admin_session_token");
          localStorage.removeItem("admin_session_expires");
          sessionStorage.setItem('pending_admin_verification', 'true');
          sessionStorage.setItem('admin_email', user.email || '');
          navigate("/admin/verify-login");
          setIsLoading(false);
          return;
        }

        console.log('✅ Valid admin user with 2FA session');
        setIsAdmin(true);
      } catch (error) {
        console.error("❌ Error checking admin role:", error);
        navigate("/");
      } finally {
        setIsLoading(false);
      }
    };

    checkAdminRole();
  }, [user, navigate]);

  return { isAdmin, isLoading };
};
